import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, LOCALE_ID, NgModule} from '@angular/core';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {TopBarComponent} from './components/topbar/topbar.component';
import {MenuComponent} from './components/menu/menu.component';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ButtonModule} from 'primeng/button';
import {ConfirmationService, MessageService, SharedModule} from 'primeng/api';
import {PanelModule} from 'primeng/panel';
import {CheckboxModule} from 'primeng/checkbox';
import {PaginatorModule} from 'primeng/paginator';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RippleModule} from 'primeng/ripple';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {MenuModule} from 'primeng/menu';
import {TableModule} from 'primeng/table';
import {SidebarModule} from 'primeng/sidebar';
import {InputTextModule} from 'primeng/inputtext';
import {RadioButtonModule} from 'primeng/radiobutton';
import {InputTextareaModule} from 'primeng/inputtextarea';
import {DropdownModule} from 'primeng/dropdown';
import {TriStateCheckboxModule} from 'primeng/tristatecheckbox';
import {MultiSelectModule} from 'primeng/multiselect';
import {TabMenuModule} from 'primeng/tabmenu';
import {CardModule} from 'primeng/card';
import {ToastModule} from 'primeng/toast';
import {MessagesModule} from 'primeng/messages';
import {MessageModule} from 'primeng/message';
import {SkeletonModule} from 'primeng/skeleton';
import {BadgeModule} from 'primeng/badge';
import {DataViewModule} from 'primeng/dataview';
import {SharedComponentsModule} from './components/shared/shared-components.module';
import {CalendarModule} from 'primeng/calendar';
import {ConfirmPopupModule} from 'primeng/confirmpopup';
import {InputSwitchModule} from 'primeng/inputswitch';
import {NgxJsonViewerModule} from 'ngx-json-viewer';
import {StepsModule} from 'primeng/steps';
import {TimelineModule} from 'primeng/timeline';
import {MachineListComponent} from './components/machines/machine-list.component';
import {MapComponent} from './components/map/map.component';
import {TranslocoRootModule} from './i18n/transloco-root.module';
import {TranslocoModule, TranslocoService} from '@ngneat/transloco';
import {ToggleButtonModule} from 'primeng/togglebutton';
import {TagModule} from 'primeng/tag';
import {ToolbarModule} from 'primeng/toolbar';
import {InputNumberModule} from 'primeng/inputnumber';
import {InputMaskModule} from 'primeng/inputmask';
import {NgxIntlTelInputModule} from 'ngx-intl-tel-input';
import {NgJsonEditorModule} from 'ang-jsoneditor';
import {SplitterModule} from 'primeng/splitter';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {TreeSelectModule} from 'primeng/treeselect';
import {DialogModule} from 'primeng/dialog';
import {ScrollPanelModule} from 'primeng/scrollpanel';
import {UsersComponent} from './components/users/users.component';
import {UsersFormComponent} from './components/users/users-form/users-form.component';
import {UserGroupsComponent} from './components/users/user-groups/user-groups.component';
import {NotFoundComponent} from './components/not-found/not-found.component';
import {CustomDatePipe} from './shared/custom-date.pipe';
import {AppHttpInterceptor} from './common/http-interceptor/filtered-http';
import {InitialAuthService} from './common/initialauth.service';
import {OAuthModule} from 'angular-oauth2-oidc';
import {MainComponent} from './main.component';
import {OverlayPanelModule} from 'primeng/overlaypanel';
import {PasswordModule} from 'primeng/password';
import {TreeModule} from 'primeng/tree';
import {ContextMenuModule} from 'primeng/contextmenu';
import {NotAuthorizedComponent} from './components/not-authorized/not-authorized.component';
import {PickListModule} from 'primeng/picklist';
import {MapMachineDetailsComponent} from './components/map/map-machine-details/map-machine-details.component';
import {CarouselModule} from 'primeng/carousel';
import {MapClusterDetailsComponent} from './components/map/map-cluster-details/map-cluster-details.component';
import {OrderListModule} from 'primeng/orderlist';
import {SliderModule} from 'primeng/slider';
import {InplaceModule} from 'primeng/inplace';
import {SelectButtonModule} from 'primeng/selectbutton';
import {MachinesFormComponent} from './components/machines/machines-form/machines-form.component';
import {PendingChangesGuard} from './common/can-deactivate/pending-changes-guard.service';
import {BeforeNavigationGuardService} from './common/can-deactivate/before-navigation-guard.service';
import {DividerModule} from 'primeng/divider';
import {AvatarModule} from 'primeng/avatar';
import {SplitButtonModule} from 'primeng/splitbutton';
import {CountryPipe} from './shared/country.pipe';
import {ColorPickerModule} from 'primeng/colorpicker';
import {OrganizationsResolverPipe} from './shared/organizations-resolver.pipe';
import {TenantsResolverPipe} from './shared/tenants-resolver.pipe';
import {ChipsModule} from "primeng/chips";
import {UserListComponent} from './components/users/user-list/user-list.component';
import {ProfileListComponent} from './components/users/profiles/profile-list/profile-list.component';
import {ProfileFormComponent} from './components/users/profiles/profile-form/profile-form.component';
import {PermissionFormComponent} from './components/users/permissions/permission-form/permission-form.component';
import {PermissionListComponent} from './components/users/permissions/permission-list/permission-list.component';
import {TreeTableModule} from "primeng/treetable";
import {MachineModelListComponent} from "./components/machine-models/machine-model-list.component";
import {MachineGroupsListComponent} from "./components/machine-groups/machine-groups-list.component";
import {
  MachineGroupsFormComponent
} from "./components/machine-groups/machine-groups-form/machine-groups-form.component";
import {OrganizationsFormComponent} from "./components/organizations/organizations-form/organizations-form.component";
import {MachineModelFormComponent} from "./components/machine-models/machine-models-form/machine-model-form.component";
import {OrganizationsListComponent} from "./components/organizations/organizations-list.component";
import {ClipboardModule} from "@angular/cdk/clipboard";
import {FirmwareListComponent} from "./components/firmware/firmware-list/firmware-list.component";
import {FirmwareProceduresListComponent} from "./components/firmware/firmware-procedures-list/firmware-procedures-list.component";
import {FirmwareComponent} from "./components/firmware/firmware.component";
import {AuditsListComponent} from "./components/audits/audits-list.component";
import {AccountComponent} from "./components/account/account.component";
import {AnalysisComponent} from "./components/analysis/analysis.component";
import {AnalysisGeneralComponent} from "./components/analysis/analysis-general/analysis-general.component";
import {AnalysisCompaniesComponent} from "./components/analysis/analysis-companies/analysis-companies.component";
import {AnalysisConsumptionComponent} from "./components/analysis/analysis-consumption/analysis-consumption.component";
import {
  AnalysisMachineParkComponent
} from "./components/analysis/analysis-machine-park/analysis-machine-park.component";
import {AccountDetailsComponent} from "./components/account/account-details/account-details.component";
import {
  AccountSubscriptionsComponent
} from "./components/account/account-subscriptions/account-subscriptions.component";
import {AccountFaqsComponent} from "./components/account/account-faqs/account-faqs.component";
import {OrganizationTypesListComponent} from "./components/organization-types/organization-types-list.component";
import {ChipModule} from "primeng/chip";

import {DatePipe, registerLocaleData} from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import {CustomCommonModule} from './common/common.module';
import {
  OrganizationSubscriptionsComponent
} from './components/organizations/organization-subscriptions/organization-subscriptions.component';
import {ClipboardLabelComponent} from "./components/shared/clipboard-label/clipboard-label.component";
import {ImportCsvComponent} from "./components/import-csv/import-csv.component";
import {
  DeleteProfileDialogComponent
} from './components/users/profiles/delete-profile-dialog/delete-profile-dialog.component';
import {AccordionModule} from "primeng/accordion";
import {SafeUrlPipe} from "./shared/safe-url.pipe";
import {AccountUserGuideComponent} from "./components/account/account-user-guide/account-user-guide.component";
import {CustomDateSubscriptionPipe} from "./shared/custom-date-subscription.pipe";
import {SubscriptionsListComponent} from './components/subscriptions/subscriptions-list.component';

registerLocaleData(localeEs, 'es');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeIt, 'it');

@NgModule({
  declarations: [
    ClipboardLabelComponent,
    ImportCsvComponent,
    MainComponent,
    AppComponent,
    TopBarComponent,
    MenuComponent,
    MachineListComponent,
    MapComponent,
    UsersComponent,
    UsersFormComponent,
    UserGroupsComponent,
    UserListComponent,
    NotFoundComponent,
    NotAuthorizedComponent,
    CustomDatePipe,
    CustomDateSubscriptionPipe,
    MapMachineDetailsComponent,
    MapClusterDetailsComponent,
    MachinesFormComponent,
    OrganizationsResolverPipe,
    TenantsResolverPipe,
    CountryPipe,
    ProfileListComponent,
    ProfileFormComponent,
    PermissionFormComponent,
    PermissionListComponent,
    MachineModelListComponent,
    MachineModelFormComponent,
    MachineGroupsListComponent,
    MachineGroupsFormComponent,
    OrganizationsFormComponent,
    OrganizationsListComponent,
    OrganizationTypesListComponent,
    FirmwareComponent,
    FirmwareListComponent,
    FirmwareProceduresListComponent,
    AuditsListComponent,
    AccountComponent,
    AccountDetailsComponent,
    AccountSubscriptionsComponent,
    AccountFaqsComponent,
    AccountUserGuideComponent,
    AnalysisComponent,
    AnalysisGeneralComponent,
    AnalysisCompaniesComponent,
    AnalysisConsumptionComponent,
    AnalysisMachineParkComponent,
    OrganizationSubscriptionsComponent,
    DeleteProfileDialogComponent,
    SafeUrlPipe,
    SubscriptionsListComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    CheckboxModule,
    PaginatorModule,
    ButtonModule,
    PanelModule,
    TableModule,
    SharedModule,
    RippleModule,
    ProgressSpinnerModule,
    MenuModule,
    SidebarModule,
    InputTextModule,
    RadioButtonModule,
    InputTextareaModule,
    DropdownModule,
    TriStateCheckboxModule,
    TreeSelectModule,
    MultiSelectModule,
    TabMenuModule,
    CardModule,
    ToastModule,
    MessagesModule,
    MessageModule,
    DialogModule,
    SkeletonModule,
    BadgeModule,
    DataViewModule,
    SharedComponentsModule,
    CalendarModule,
    ConfirmPopupModule,
    InputSwitchModule,
    NgxJsonViewerModule,
    StepsModule,
    ReactiveFormsModule,
    TimelineModule,
    TranslocoRootModule,
    ToggleButtonModule,
    TagModule,
    ToolbarModule,
    InputNumberModule,
    InputMaskModule,
    InputSwitchModule,
    NgxIntlTelInputModule,
    NgJsonEditorModule,
    SplitterModule,
    ConfirmPopupModule,
    ConfirmDialogModule,
    PasswordModule,
    TreeModule,
    ContextMenuModule,
    DialogModule,
    ScrollPanelModule,
    OverlayPanelModule,
    OrderListModule,
    InplaceModule,
    SliderModule,
    PickListModule,
    SelectButtonModule,
    SplitButtonModule,
    TreeTableModule,
    TreeModule,
    OAuthModule.forRoot({
      resourceServer: {
        sendAccessToken: true
      }
    }),
    CarouselModule,
    DividerModule,
    AvatarModule,
    ColorPickerModule,
    CustomCommonModule,
    ChipsModule,
    ClipboardModule,
    ChipModule,
    AccordionModule
  ],
  exports: [
    TranslocoModule
  ],
  providers: [
    ConfirmationService,
    PendingChangesGuard,
    MessageService,
    BeforeNavigationGuardService,
    {provide: HTTP_INTERCEPTORS, useClass: AppHttpInterceptor, multi: true},
    InitialAuthService,
    {
      provide: APP_INITIALIZER,
      useFactory: (initialAuthService: InitialAuthService) => () => initialAuthService.initAuth(),
      deps: [InitialAuthService],
      multi: true
    },
    {
      provide: LOCALE_ID,
      deps: [TranslocoService],
      useFactory: (translocoService) => translocoService.getActiveLang()
    }, DatePipe,
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
